import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.letsnote.app';
const WS_BASE = import.meta.env.VITE_WS_URL || 'wss://api.letsnote.app';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add password to requests if available
api.interceptors.request.use((config) => {
  const listId = window.location.pathname.slice(1);
  const password = sessionStorage.getItem(`password_${listId}`);
  
  if (password) {
    config.headers['X-List-Password'] = password;
  }
  
  return config;
});

// API methods
export const listAPI = {
  // Get or create a list
  async getList(listId) {
    try {
      const response = await api.get(`/lists/${listId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        // List doesn't exist, create it
        return this.createList(listId);
      }
      throw error;
    }
  },

  // Add this to the listAPI object in client.js
  async setPassword(listId, password) {
    const response = await api.post(`/lists/${listId}/set-password`, { password });
    return response.data;
  },
  
  // Create a new list
  async createList(listId) {
    const response = await api.post('/lists', { listId });
    return response.data;
  },
  
  // Update list content
  async updateContent(listId, content) {
    const response = await api.put(`/lists/${listId}`, { content });
    return response.data;
  },
  
  // Lock a list (initiates Stripe checkout)
  async lockList(listId, email) {
    const response = await api.post(`/lists/${listId}/lock`, { email });
    return response.data;
  },
  
  // Verify password for editing
  async verifyPassword(listId, password) {
    const response = await api.post(`/lists/${listId}/verify`, { password });
    return response.data;
  },
  
  // Handle Stripe success callback
  async confirmLock(listId, sessionId) {
    const response = await api.post(`/lists/${listId}/confirm-lock`, { sessionId });
    return response.data;
  },
  
  // Cancel subscription
  async cancelSubscription(listId, email) {
    const response = await api.post(`/lists/${listId}/cancel`, { email });
    return response.data;
  },
};

// WebSocket connection manager
export class ListWebSocket {
  constructor(listId, onMessage, onStatusChange) {
    this.listId = listId;
    this.onMessage = onMessage;
    this.onStatusChange = onStatusChange;
    this.ws = null;
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }
  
  connect() {
    const password = sessionStorage.getItem(`password_${this.listId}`);
    const params = password ? `?password=${encodeURIComponent(password)}` : '';
    
    this.ws = new WebSocket(`${WS_BASE}/ws/${this.listId}${params}`);
    
    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.onStatusChange({ connected: true });
      
      // Start keep-alive ping
      this.startKeepAlive();
    };
    
    // Add pong handling in onmessage
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong') {
          this.lastPong = Date.now();
          return;
        }
        this.onMessage(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };
    
    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      this.onStatusChange({ connected: false });
      this.attemptReconnect();
    };
    
    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.onStatusChange({ connected: false, error: true });
    };
  }
  startKeepAlive() {
    this.lastPong = Date.now();
    
    this.keepAliveTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Check if we got a recent pong
        if (Date.now() - this.lastPong > 60000) { // No pong for 1 minute
          console.log('Connection seems dead, reconnecting...');
          this.attemptReconnect();
        } else {
          this.send({ type: 'ping' });
        }
      }
    }, 15000);
  }
  
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return;
    }
    
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      console.log(`Reconnecting... (attempt ${this.reconnectAttempts})`);
      this.connect();
    }, this.reconnectDelay);
    
    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
  }
  
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
  
  disconnect() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
    }
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

export default api;